<template>
  <div>
    <!-- TKP-200 m.21: kaydet başlıkla birlikte yapışkan üst çubukta — kaydırmadan görünür; değişiklik varsa uyarı -->
    <div class="d-flex align-items-center justify-content-between bg-white py-2 mb-2" style="position: sticky; top: 0; z-index: 20;" data-test="save-bar">
      <h2 class="mb-0">{{ $t("module-settings.title") }}</h2>
      <div v-if="form" class="d-flex align-items-center">
        <small v-if="dirty" class="text-warning mr-3" data-test="unsaved">{{ $t("module-settings.unsaved") }}</small>
        <sweet-button @click="save()" :loading="isUpdating" :disabled="isUpdating || !dirty" variant="success" icon="mdi mdi-check" size="sm" data-test="save">
          {{ $t("common.save") }}
        </sweet-button>
      </div>
    </div>

    <div class="card">
      <div class="card-body" v-if="form">
        <!-- TKP-238 a.1: ayarlar sekmelere ayrıldı (Görevler › Görevler görünümü, nav-tab-menu) -->
        <b-tabs nav-class="nav-tab-menu mb-3" data-test="settings-tabs">
          <b-tab :title="$t('module-settings.tabs.inventory')" data-test="tab-inventory">
            <b-form-checkbox switch size="lg" v-model="form.inventoryApprovalEnabled" data-test="inventory-approval">
              {{ $t("module-settings.inventory.approval-enabled") }}
            </b-form-checkbox>
            <!-- TKP-238 a.3: bilgi metni bir punto büyük -->
            <div class="text-muted mb-3" data-test="inventory-approval-hint">{{ $t("module-settings.inventory.approval-enabled-hint") }}</div>

            <!-- TKP-134 / TKP-238 a.6–a.10: onay modu — yalnız onay süreci açıkken; organizasyon ve birim bazında. TKP-240 b.9: onaylayacak rol de -->
            <template v-if="form.inventoryApprovalEnabled">
              <label class="font-weight-bold d-block">{{ $t("module-settings.inventory.approver-role") }}</label>
              <select class="form-control mb-3" style="max-width: 420px;" v-model="form.inventoryApproverRole" data-test="approver-role">
                <option v-for="role in settings.approverRoleOptions" :key="role" :value="role">{{ $t(approverRoleLabelKey(role)) }}</option>
              </select>
              <label class="font-weight-bold d-block">{{ $t("module-settings.inventory.approval-mode.title") }}</label>
              <select class="form-control mb-3" style="max-width: 560px;" v-model="form.approvalMode" data-test="approval-mode">
                <option v-for="mode in approvalModes" :key="mode" :value="mode">{{ $t(approvalModeLabelKey(mode)) }}</option>
              </select>
              <div v-if="departments.length" class="mb-4" data-test="department-modes">
                <label class="font-weight-bold d-block">{{ $t("module-settings.inventory.approval-mode.department-title") }}</label>
                <table class="table table-sm mb-1" style="max-width: 900px;">
                  <thead>
                    <tr>
                      <th>{{ $t("approval.department") }}</th>
                      <th>{{ $t("module-settings.inventory.approval-mode.column") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="department in departments" :key="department._id" :data-test="`department-mode-${department._id}`">
                      <td class="align-middle">{{ department.name | translate }}</td>
                      <td>
                        <select class="form-control form-control-sm" :value="departmentMode(department._id)" @change="setDepartmentMode(department._id, $event.target.value)">
                          <option v-for="mode in departmentModes" :key="mode" :value="mode">{{ $t(approvalModeLabelKey(mode)) }}</option>
                        </select>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <small class="d-block text-muted">{{ $t("module-settings.inventory.approval-mode.department-hint") }}</small>
              </div>
            </template>

            <!-- TKP-154 / TKP-238 a.11: envanterin periyodik olarak dosyaya kaydedilme süresi -->
            <label class="font-weight-bold d-block">{{ $t("module-settings.snapshot.months") }}</label>
            <select class="form-control" style="max-width: 420px;" v-model.number="form.inventorySnapshotMonths" data-test="snapshot-months">
              <option v-for="months in snapshotOptions" :key="months" :value="months">{{ months === 0 ? $t("module-settings.snapshot.none") : $t("module-settings.snapshot.every", { months }) }}</option>
            </select>
            <small class="d-block text-muted">{{ $t("module-settings.snapshot.months-hint") }}</small>
            <small class="d-block text-muted" data-test="snapshot-last">{{ settings && settings.inventorySnapshotLastAt ? $t("module-settings.snapshot.last", { at: $options.filters.date(settings.inventorySnapshotLastAt, true) }) : $t("module-settings.snapshot.last-none") }}</small>
          </b-tab>

          <b-tab :title="$t('module-settings.tabs.periodic')" data-test="tab-periodic">
            <!-- TKP-107 / md. 46: periyodik güncelleme dönemi -->
            <label class="font-weight-bold d-block">{{ $t("module-settings.periodic.months") }}</label>
            <select class="form-control" style="max-width: 420px;" v-model.number="form.periodicUpdateMonths" data-test="periodic-months">
              <option v-for="months in periodicOptions" :key="months" :value="months">{{ periodLabel(months) }}</option>
            </select>
            <small class="d-block text-muted mb-4">{{ $t("module-settings.periodic.months-hint") }}</small>
            <!-- TKP-135 / TKP-238 a.13–a.15: birim bazında dönem; 0 = birimde periyodik güncelleme yok -->
            <div v-if="departments.length" class="mb-4" data-test="department-periods">
              <label class="font-weight-bold d-block">{{ $t("module-settings.periodic.department-title") }}</label>
              <table class="table table-sm mb-1" style="max-width: 820px;">
                <thead>
                  <tr>
                    <th>{{ $t("approval.department") }}</th>
                    <th>{{ $t("module-settings.periodic.department-column") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="department in departments" :key="department._id">
                    <td class="align-middle">{{ department.name | translate }}</td>
                    <td>
                      <select class="form-control form-control-sm" :value="departmentPeriod(department._id)" :data-test="`department-period-${department._id}`" @change="setDepartmentPeriod(department._id, $event.target.value)">
                        <option v-for="months in periodicOptions" :key="months" :value="months">{{ periodLabel(months) }}</option>
                      </select>
                    </td>
                  </tr>
                </tbody>
              </table>
              <small class="d-block text-muted">{{ $t("module-settings.periodic.department-hint") }}</small>
            </div>
            <!-- TKP-179 md.62 / TKP-238 a.16–a.18: otomatik başlatma — dönem tanımlı değilse gizli -->
            <template v-if="periodsDefined">
              <b-form-checkbox switch size="lg" v-model="form.periodicAutoStart" data-test="periodic-auto-start">{{ $t("module-settings.periodic.auto-start") }}</b-form-checkbox>
              <small class="d-block text-muted" style="white-space: pre-line;">{{ $t("module-settings.periodic.auto-start-hint") }}</small>
            </template>
          </b-tab>

          <b-tab :title="$t('module-settings.tabs.email')" data-test="tab-email">
            <!-- TKP-107 / md. 7, 32, 49: e-posta gönderim konuları -->
            <b-form-checkbox v-for="topic in emailTopics" :key="topic" switch size="lg" v-model="form.emailTopics[topic]" :data-test="`email-${topic}`">
              {{ $t(`module-settings.email.${topic}`) }}
            </b-form-checkbox>
            <small class="d-block text-muted mb-4">{{ $t("module-settings.email.hint") }}</small>

            <!-- TKP-238 a.23: düzenlenebilir e-posta metinleri (Görevler › Ayarlar › E-posta Ayarları gibi); boş alan varsayılanı kullanır -->
            <h6>{{ $t("module-settings.email.texts") }}</h6>
            <p class="small text-muted mb-2">{{ $t("module-settings.email.texts-hint") }}</p>
            <b-card v-for="key in templateKeys" :key="key" no-body class="mb-1 shadow-none border" :data-test="'mail-template-' + key">
              <b-card-header class="py-1 px-2 hand d-flex align-items-center" @click="openTemplate = openTemplate === key ? null : key">
                <i :class="openTemplate === key ? 'bx bx-chevron-down' : 'bx bx-chevron-right'" class="mr-1"></i>
                <span class="small">{{ $t("module-settings.email.template." + key + ".title") }}</span>
                <b-badge v-if="customized(key)" variant="info" class="ml-2" data-test="mail-template-custom">{{ $t("module-settings.email.customized") }}</b-badge>
              </b-card-header>
              <b-collapse :visible="openTemplate === key">
                <b-card-body class="p-2">
                  <p class="small text-muted mb-2" :data-test="'mail-variables-' + key">
                    <span v-for="v in mailVariables[key]" :key="v" class="mr-2"><code>{{ "{" + v + "}" }}</code> {{ variableLabel(key, v) }}</span>
                  </p>
                  <input type="text" class="form-control form-control-sm mb-2" maxlength="200" :value="templateText(key, 'subject')" :placeholder="$t('module-settings.email.template.' + key + '.subject')" :data-test="'mail-template-' + key + '-subject'" @input="(e) => setTemplateText(key, 'subject', e.target.value)" />
                  <textarea class="form-control form-control-sm" rows="4" maxlength="5000" :value="templateText(key, 'body')" :placeholder="$t('module-settings.email.template.' + key + '.body')" :data-test="'mail-template-' + key + '-body'" @input="(e) => setTemplateText(key, 'body', e.target.value)"></textarea>
                  <div class="small text-muted mt-1">
                    <a v-if="customized(key)" href="javascript:;" :data-test="'mail-template-' + key + '-reset'" @click="resetTemplate(key)">{{ $t("module-settings.email.reset") }}</a>
                  </div>
                </b-card-body>
              </b-collapse>
            </b-card>
          </b-tab>

          <b-tab :title="$t('module-settings.tabs.legal')" data-test="tab-legal">
            <b-form-checkbox switch size="lg" v-model="form.legalDocumentApprovalRequired" data-test="legal-approval">
              {{ $t("module-settings.legal-document.approval-required") }}
            </b-form-checkbox>
            <small class="d-block text-muted">{{ $t("module-settings.legal-document.approval-required-hint") }}</small>
          </b-tab>
        </b-tabs>
      </div>

      <div class="card-body" v-else-if="forbidden" data-test="forbidden">
        {{ $t("module-settings.forbidden") }}
      </div>
    </div>
  </div>
</template>

<script>
import { APPROVAL_MODES, approvalModeLabelKey, approverRoleLabelKey, changedSettings, DEPARTMENT_APPROVAL_MODES, EMAIL_TOPICS, formFrom, hasChanges, hasPeriods, MAIL_TEMPLATE_KEYS, MAIL_VARIABLES, PERIODIC_MONTHS_OPTIONS, SNAPSHOT_MONTHS_OPTIONS } from "./module-settings";

/** TKP-89 / tasarım s.21 — KVKK ve GDPR modülü Ayarlar ekranı (organizasyon × modül). */
export default {
  data() {
    return { form: null, forbidden: false, emailTopics: EMAIL_TOPICS, periodicOptions: PERIODIC_MONTHS_OPTIONS, snapshotOptions: SNAPSHOT_MONTHS_OPTIONS, approvalModes: APPROVAL_MODES, departmentModes: DEPARTMENT_APPROVAL_MODES, templateKeys: MAIL_TEMPLATE_KEYS, mailVariables: MAIL_VARIABLES, openTemplate: null, departments: [] };
  },
  computed: {
    settings() {
      return this.$store.getters["moduleSettings/settings"];
    },
    isUpdating() {
      return this.$store.getters["moduleSettings/isUpdating"];
    },
    dirty() {
      return hasChanges(this.settings, this.form);
    },
    periodsDefined() {
      return hasPeriods(this.form);
    },
  },
  /** TKP-200 m.21: kaydedilmemiş değişiklikle ekrandan çıkılırsa "kaydetmek ister misiniz?" — Evet kaydeder, Hayır bırakır. */
  async beforeRouteLeave(to, from, next) {
    next(await this.confirmLeave());
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.onBeforeUnload);
  },
  async mounted() {
    window.addEventListener("beforeunload", this.onBeforeUnload);
    await this.load();
    await this.loadDepartments();
  },
  methods: {
    approverRoleLabelKey,
    approvalModeLabelKey,
    /** TKP-134: birim listesi (mod tablosu için); hata ekranı düşürmez, tablo çizilmez. */
    async loadDepartments() {
      try {
        const response = await this.$store.dispatch("organizationDepartment/paginate", { options: { limit: 500, sort: { "name.tr": "asc" } }, query: {} });
        this.departments = (response && response.data && response.data.docs) || [];
      } catch (error) {
        this.departments = [];
      }
    },
    periodLabel(months) {
      return months === 0 ? this.$t("module-settings.periodic.none") : this.$t("module-settings.periodic.every", { months });
    },
    /** TKP-238 a.13: birimde geçerli dönem — kendi kaydı (0 dahil) ya da organizasyonun dönemi. */
    departmentPeriod(departmentId) {
      const own = this.form.departmentPeriodMonths[departmentId];
      return typeof own === "number" ? own : Number(this.form.periodicUpdateMonths) || 0;
    },
    /** Organizasyonun dönemiyle aynı seçim birim kaydını kaldırır (birim organizasyonu izler); farklıysa birim kaydı (0 = yok). */
    setDepartmentPeriod(departmentId, months) {
      const next = { ...this.form.departmentPeriodMonths };
      if (Number(months) === (Number(this.form.periodicUpdateMonths) || 0)) delete next[departmentId];
      else next[departmentId] = Number(months);
      this.form = { ...this.form, departmentPeriodMonths: next };
    },
    /** TKP-238 a.9: birimde geçerli mod — kendi kaydı ya da organizasyonun modu. */
    departmentMode(departmentId) {
      return this.form.departmentApprovalModes[departmentId] || this.form.approvalMode || "STANDARD";
    },
    setDepartmentMode(departmentId, mode) {
      const next = { ...this.form.departmentApprovalModes };
      if (!mode || mode === (this.form.approvalMode || "STANDARD")) delete next[departmentId];
      else next[departmentId] = mode;
      this.form = { ...this.form, departmentApprovalModes: next };
    },
    /** TKP-249 e.6: şablona özel değişken etiketi (ör. hatırlatmada {count} birim sayısı), yoksa ortak etiket. */
    variableLabel(key, v) {
      const own = `module-settings.email.variable-${key}.${v}`;
      return typeof this.$te === "function" && this.$te(own) ? this.$t(own) : this.$t(`module-settings.email.variable.${v}`);
    },
    templateText(key, field) {
      return (this.form.emailTemplates[key] && this.form.emailTemplates[key][field]) || "";
    },
    setTemplateText(key, field, value) {
      this.form = { ...this.form, emailTemplates: { ...this.form.emailTemplates, [key]: { ...(this.form.emailTemplates[key] || {}), [field]: value } } };
    },
    customized(key) {
      const t = this.form.emailTemplates[key] || {};
      return !!(String(t.subject || "").trim() || String(t.body || "").trim());
    },
    resetTemplate(key) {
      const { [key]: _drop, ...rest } = this.form.emailTemplates;
      this.form = { ...this.form, emailTemplates: rest };
    },
    async load() {
      try {
        const { data } = await this.$store.dispatch("moduleSettings/fetch");
        this.form = formFrom(data);
      } catch (error) {
        this.forbidden = true;
      }
    },
    /** Çıkışa izin verilir mi: değişiklik yoksa evet; varsa sorar, Evet ise kaydeder (kayıt hata verirse çıkılmaz). */
    async confirmLeave() {
      if (!this.form || !this.dirty) return true;
      const wantsSave = await this.confirm(this.$t("module-settings.unsaved-confirm"));
      if (!wantsSave) return true;
      return this.save();
    },
    onBeforeUnload(event) {
      if (!this.form || !this.dirty) return;
      event.preventDefault();
      event.returnValue = "";
    },
    async save() {
      try {
        const { data } = await this.$store.dispatch("moduleSettings/update", changedSettings(this.settings, this.form));
        this.form = formFrom(data);
        this.$toast.success(this.$t("common.success"));
        return true;
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
        return false;
      }
    },
  },
};
</script>
